import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getFromStorage } from 'utils/storageUtils';
import { AxiosError } from 'axios';

import { useAuth } from './authContext';
import useSubscription from './contextsHooks/useSubscription';

import UserService from 'services/UserService';
import useIsMobile from 'hooks/useIsMobile';

import { keyRole, isUserGuide, isB2BUser } from 'utils/isRole';
import { B2B_ROUTES } from 'constants/routes';
import isUnloggedRoute from 'utils/isUnloggedRoute';

import { CURRENCIES } from 'constants/currencies';
import { CONTENTA_TOKEN } from 'constants/storages-values';
import { USER_TYPES } from 'constants/types';

const UserContext = createContext({});

let timeout = null;

const REGISTER_PAGES = ['/register', '/redirecting', '/guide/register'];

// consumers read `userCurrency.currencyStandard`, so the fallback has to keep
// the same shape as the API response instead of being a bare string
const DEFAULT_CURRENCY = {
  currencyStandard: 'USD',
  countryName: 'USD',
  name: 'USD',
};

const helperFields = {
  isAdult: true,
  birthDate: null,
  isValidDate: true,
};

export function UserProvider({ children }) {
  const { subscription, getSubscription, subscriptionError } =
    useSubscription();
  const navigate = useNavigate();

  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  const {
    authUser,
    isAuthenticated,
    isLoading,
    setAuthUser,
    setIsAuthenticated,
    setIsLoading,
    updateLocalToken,
  } = useAuth();

  const [loggedUser, setLoggedUser] = useState({});
  const [userCurrency, setUserCurrency] = useState(DEFAULT_CURRENCY);
  const [userError, setUserError] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [whoAmI, setWhoAmI] = useState({});
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showBookflowModal, setShowBookflowModal] = useState(false);
  const [registerFieldsHelper, setRegisterFieldsHelper] =
    useState(helperFields);
  const hasFetchedUserRef = useRef(false);
  const getLoggedUserRef = useRef(null);

  const isRegisterPage = useMemo(
    () => REGISTER_PAGES.includes(pathname),
    [pathname]
  );

  // The /logout page clears storage and then leaves the app through Auth0's
  // logout URL, so nothing is cleared here. Dropping the token first made the
  // in-flight requests answer 401 and the api interceptor hard-redirected to
  // the login page before Auth0 ended the session: the SSO cookie survived and
  // the next login on the same browser reused the previous user.
  const logout = useCallback(() => {
    // Auth0 only allows the site root as returnTo, so the /logout page stores
    // this destination and the public layout applies it after the round trip.
    const returnTo = isB2BUser(authUser) ? B2B_ROUTES.LOGIN : null;
    navigate('/logout', { state: returnTo ? { returnTo } : undefined });
  }, [navigate, authUser]);

  const handleUserContext = useCallback((user) => {
    setLoggedUser(user);
  }, []);

  const getUserCurrency = useCallback(async () => {
    try {
      const response = await UserService.getUserCurrency();
      const countryByCurrency = CURRENCIES.find(
        (currency) => currency.code === response.currencyStandard
      ) ?? { name: 'USD' };

      setUserCurrency({
        ...response,
        countryName: response.name,
        name: countryByCurrency.name,
      });
    } catch {
      setUserCurrency(DEFAULT_CURRENCY);
    }
  }, []);

  const handleError = useCallback(
    (error) => {
      setWhoAmI({ ...loggedUser, ...authUser, isRegisterPage });

      const { pathname: currentPath } = window.location;
      if (currentPath.includes('/register') || currentPath === '/') return;

      if (error instanceof AxiosError) {
        setUserError(error);
        const status = error.response?.status ?? 0;
        const isUnloggedPage = isUnloggedRoute(currentPath);

        if (isUnloggedPage) {
          setIsLoading(false);
          return;
        }

        const isAdmin = authUser?.[keyRole]?.[0] === USER_TYPES.admin;
        if (status === 404 && !isAdmin) {
          setIsLoading(false);
          navigate('/guide/register');
          return;
        }

        const isAdvisorUserEndpoint =
          error.response?.request?.responseURL?.includes('advisor/user');

        if (isAdvisorUserEndpoint && !isUnloggedPage && !isAdmin) {
          if (timeout) clearTimeout(timeout);

          // getLoggedUser is defined below and depends on handleError. Going
          // through the ref breaks that cycle without leaving the retry on a
          // stale closure.
          timeout = setTimeout(() => {
            getLoggedUserRef.current?.();
          }, 10000);
        }
      }
    },
    [authUser, isRegisterPage, loggedUser, navigate, setIsLoading]
  );

  const getLoggedUser = useCallback(async () => {
    try {
      setIsLoadingUser(true);
      const role = authUser[keyRole]?.[0];
      setUserRole(role || '');

      // B2B users have no advisor/traveler profile — skip the profile fetch
      if (isB2BUser(authUser)) {
        setWhoAmI({ ...loggedUser, ...authUser, isRegisterPage });
        setUserError(null);
        return;
      }

      const userRoles = authUser[keyRole] || [];
      const userRolesWithoutAdmin = userRoles.filter(
        (r) => r !== USER_TYPES.admin
      );

      const data = await UserService.getCurrentUser({
        role: userRolesWithoutAdmin[0],
      });

      // Verificar se o guia tem switchToBookflow ativado
      if (isUserGuide(role) && data.switchToBookflow === true) {
        setShowBookflowModal(true);
      }

      setWhoAmI({
        ...loggedUser,
        ...authUser,
        ...data,
        isRegisterPage,
      });
      setUserError(null);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoadingUser(false);
    }
  }, [authUser, handleError, isRegisterPage, loggedUser]);

  useEffect(() => {
    getLoggedUserRef.current = getLoggedUser;
  }, [getLoggedUser]);

  const updateUserData = useCallback(
    (data) => {
      setWhoAmI({ ...loggedUser, ...data });
    },
    [loggedUser]
  );

  const reloadUserData = useCallback(async () => {
    await getLoggedUser();
  }, [getLoggedUser]);

  const fetchUser = useCallback(async () => {
    hasFetchedUserRef.current = true;
    setLoggedUser(authUser);
    await Promise.all([
      getLoggedUser(),
      getSubscription(authUser[keyRole]?.[0]),
    ]);
  }, [authUser, getLoggedUser, getSubscription]);

  const setToken = useCallback(
    async (token) => {
      setLoggedUser({ ...loggedUser, token });
      updateLocalToken(token);
    },
    [loggedUser, updateLocalToken]
  );

  useEffect(() => {
    if (isLoading) return;

    const hasUserInfo = Object.keys(authUser).length > 0;

    if (hasUserInfo && !hasFetchedUserRef.current) {
      fetchUser();
    } else if (!hasUserInfo) {
      setIsLoadingUser(false);
    }
    // fetchUser is intentionally omitted: the ref prevents re-entry; authUser
    // change already guarantees a fresh fetchUser reference when needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, isLoading]);

  useEffect(() => {
    getUserCurrency();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (whoAmI?.id) {
      setIsAuthenticated(true);
    }
  }, [whoAmI?.id, setIsAuthenticated]);

  const value = useMemo(
    () => ({
      authUser,
      getToken: () => getFromStorage(CONTENTA_TOKEN),
      handleUserContext,
      isAuthenticated,
      isLoading,
      isLoadingUser,
      isMobile,
      logout,
      registerFieldsHelper,
      reloadUserData,
      setAuthUser,
      setIsLoading,
      setRegisterFieldsHelper,
      setShowBookflowModal,
      setToken,
      showBookflowModal,
      subscription,
      subscriptionError,
      updateUserData,
      user: loggedUser,
      userCurrency,
      userError,
      userRole,
      whoAmI,
      whoAmILoading: isLoading,
    }),
    // The callbacks belong here: setToken and updateUserData close over
    // loggedUser, so leaving them out handed consumers a stale copy that wiped
    // newer fields on write.
    [
      authUser,
      handleUserContext,
      isAuthenticated,
      isLoading,
      isLoadingUser,
      isMobile,
      loggedUser,
      logout,
      registerFieldsHelper,
      reloadUserData,
      setAuthUser,
      setIsLoading,
      setToken,
      showBookflowModal,
      subscription,
      subscriptionError,
      updateUserData,
      userCurrency,
      userError,
      userRole,
      whoAmI,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export const useUser = () => useContext(UserContext);
